import React from "react";

// styles
import css from "./EnvironmentStatus.styl";

// dependencies
import { CheckCircle, AlertCircle, XCircle } from "lucide-react";

// utilities
import { createDesktopNotification } from "../../../Utils/sendDesktopNotification";

/**
 * This function builds the object for a single
 * row of the EnvironmentStatusTable
 * @param {string} name
 * @param {Object} environmentStatus
 * @returns Object
 */
function buildEnvironmentStatusRow(name, environmentStatus) {
	const gitBranch = environmentStatus?.gitBranch || "ERROR";
	const gitHashShort = environmentStatus?.gitHashShort || "ERROR";
	const gitHash = environmentStatus?.gitHash || "ERROR";
	const healthy = environmentStatus?.healthy;
	const healthUrl = environmentStatus?.healthUrl || "ERROR";
	return {
		name,
		gitBranch,
		gitHashShort,
		gitHash,
		healthy,
		healthUrl,
	};
}

/**
 * This function builds the array of rows for an EnvironmentStatusTable
 * @param {Object} webServer
 * @param {Object} apiServer
 * @param {Object} reportingServer
 * @param {Object} internalApi
 * @returns array
 */
export function formatEnvironmentStatusDataForTableLiteRows(webServer, apiServer, reportingServer, internalApi) {
	const rows = [];
	if (webServer) {
		rows.push(buildEnvironmentStatusRow("Web Server", webServer));
	}
	if (apiServer) {
		rows.push(buildEnvironmentStatusRow("API Server", apiServer));
	}
	if (reportingServer) {
		rows.push(buildEnvironmentStatusRow("Reporting Server", reportingServer));
	}
	if (internalApi) {
		rows.push(buildEnvironmentStatusRow("Internal API", internalApi));
	}

	return rows;
}

/**
 * @param {Object} server
 * @returns bool
 */
function calculateIfSingleServerIsHealthyOrNull(server) {
	return server === null || (server.healthy && server.gitHash);
}

/**
 * This function calculates if the server's overall state is good, if any part
 * is down, or if any gitHashes are mismatched and return a status for each
 * @param {Object} environmentStatusData
 * @returns Icon
 */
export function calculateServerOverallStateIcon(webServer, apiServer, reportingServer) {
	let overallHealth = <XCircle className={css.error} size={16} />;

	if (
		calculateIfSingleServerIsHealthyOrNull(webServer) &&
		calculateIfSingleServerIsHealthyOrNull(apiServer) &&
		calculateIfSingleServerIsHealthyOrNull(reportingServer)
	) {
		if (
			(!apiServer || webServer.gitHash === apiServer.gitHash) &&
			(!reportingServer || webServer.gitHash === reportingServer.gitHash)
		) {
			overallHealth = <CheckCircle className={css.success} size={16} />;
		} else {
			overallHealth = <AlertCircle className={css.warning} size={16} />;
		}
	}

	return overallHealth;
}

/**
 * This function checks if the hashHistort array follows a pattern
 * [A, B, B, B, B], A Desktop Notificaiton is sent
 * @param {array} hashHistory
 * @param {string} serverTitle
 */
export function sendNotificationIfServerFlip(hashHistory, serverTitle) {
	if (
		hashHistory.length === 5 &&
		hashHistory[0] !== hashHistory[1] &&
		Array.from(new Set(hashHistory.slice(1, 5))).length === 1
	) {
		createDesktopNotification("Server change detected!", `${serverTitle} has changed`);
	}
}
